import type { Pack } from '@/lib/api/packs/packs-api'
import { formatPrice } from '../account/account-utils'
import { useState, type ReactNode } from 'react'
import { MdShoppingCart } from 'react-icons/md'
import type { PackProduct } from './store-products'

const integer = new Intl.NumberFormat('pt-BR')

interface Props {
    product: PackProduct
    /** Pacotes prontos do catálogo, já com o preço vigente. */
    packs: Pack[]
    /** Linha de consumo do plano, quando o produto tem cota mensal. */
    usage?: ReactNode
    highlighted: boolean
    buying: boolean
    /** Motivo para não vender (ex.: conta da plataforma); some com a compra. */
    unavailableReason?: string
    onBuyPack: (code: string) => void
    onBuyCustom: (quantity: number) => void
}

/**
 * Um produto da Loja: pacotes prontos e quantidade personalizada. Sem preço
 * configurado no painel MASTER, o produto aparece como indisponível em vez de
 * sumir, para quem veio por um link direto entender o motivo.
 */
export function PackProductCard({
    product,
    packs,
    usage,
    highlighted,
    buying,
    unavailableReason,
    onBuyPack,
    onBuyCustom,
}: Props) {
    const [quantity, setQuantity] = useState(product.minCustomQuantity)

    const pricedPacks = packs.filter(pack => pack.price > 0)
    const reference = pricedPacks[0]
    const unitPrice = reference ? reference.price / reference.quantity : 0
    const available = unitPrice > 0
    const quantityValid = Number.isInteger(quantity) && quantity >= product.minCustomQuantity

    return (
        <section
            id={`product-${product.type}`}
            className={`card bg-base-100 border scroll-mt-24 transition-shadow ${
                highlighted ? 'border-primary ring-2 ring-primary/30' : 'border-base-300'
            }`}
        >
            <div className="card-body p-5 sm:p-6 gap-5">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        {product.icon}
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold">{product.title}</h2>
                        <p className="text-sm text-base-content/60 mt-0.5">{product.description}</p>
                        {usage && <div className="text-sm mt-2">{usage}</div>}
                    </div>
                </div>

                {unavailableReason || !available ? (
                    <div className="rounded-box bg-base-200 px-4 py-3 text-sm text-base-content/70">
                        {unavailableReason ??
                            'Indisponível no momento. Fale com o suporte se precisar deste recurso.'}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {pricedPacks.map(pack => (
                                <div
                                    key={pack.code}
                                    className="rounded-box border border-base-300 p-4 flex flex-col gap-3"
                                >
                                    <div>
                                        <p className="font-semibold">
                                            {integer.format(pack.quantity)} {product.unit}
                                        </p>
                                        <p className="text-xl font-bold text-primary tabular-nums mt-1">
                                            {formatPrice(pack.price)}
                                        </p>
                                        <p className="text-xs text-base-content/50">
                                            {formatPrice(pack.price / pack.quantity)} por unidade
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm mt-auto"
                                        disabled={buying}
                                        onClick={() => onBuyPack(pack.code)}
                                    >
                                        <MdShoppingCart />
                                        Comprar
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="rounded-box bg-base-200/60 p-4">
                            <div className="flex flex-wrap items-end gap-3">
                                <div className="form-control w-full sm:w-56">
                                    <label className="label pb-1.5" htmlFor={`custom-${product.type}`}>
                                        <span className="label-text font-medium">
                                            Outra quantidade
                                        </span>
                                    </label>
                                    <input
                                        id={`custom-${product.type}`}
                                        type="number"
                                        min={product.minCustomQuantity}
                                        step={product.minCustomQuantity >= 1000 ? 100 : 10}
                                        className="input input-bordered w-full tabular-nums"
                                        value={Number.isNaN(quantity) ? '' : quantity}
                                        onChange={event => setQuantity(parseInt(event.target.value, 10))}
                                    />
                                    <span className="label-text-alt text-base-content/50 mt-1.5">
                                        Mínimo de {integer.format(product.minCustomQuantity)} {product.unit}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-40 pb-6">
                                    {quantityValid && (
                                        <p className="text-sm text-base-content/70">
                                            {integer.format(quantity)} × {formatPrice(unitPrice)} ={' '}
                                            <span className="font-bold text-base-content tabular-nums">
                                                {formatPrice(Math.round(quantity * unitPrice))}
                                            </span>
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-outline btn-primary mb-6"
                                    disabled={buying || !quantityValid}
                                    onClick={() => onBuyCustom(quantity)}
                                >
                                    <MdShoppingCart />
                                    Comprar
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    )
}
